import React from "react";

import {EAnalysisTasks, EModules} from "@/models";
import http from "@/services/http";
import {
    IModuleGroup,
    IModuleGroupDevices,
    IModulesResponse,
    IModuleTaskGroup,
    IModuleTaskGroupEntry,
} from "@shared/analysis/analyze-button/models";
import {EServerId} from "@shared/models";
import {EDeviceClasses} from "@toolbox/models";

import {trademark} from "@/Footer";
import AnalyzeButton from "@shared/analysis/analyze-button/Index";
import T from "@translate/T";

export async function getAvailableModules(
    targets: string,
    searchParams?: {deviceClass: number; wavelengths: number},
): Promise<IModuleGroup[]> {
    const groups: IModuleGroup[] = [];

    if (!targets && !searchParams) {
        return groups;
    }

    let modules: IModulesResponse;
    try {
        modules = await http
            .get(`/api/centrifuge/analysis/modules?${targets}`, {searchParams})
            .json<IModulesResponse>();

        if (!modules.modules) {
            return groups;
        }
    } catch {
        return groups;
    }

    groups.push(
        {
            header: (
                <React.Fragment>
                    <T>LUMView</T>
                    {trademark()}
                </React.Fragment>
            ),
            id: "lum",
            modules: [EModules.LumView],
        },
        {
            header: <T>Basic analysis</T>,
            id: "standard",
            modules: [
                EModules.Instability,
                EModules.Integration,
                EModules.FrontTracking,
            ],
        },
        {
            header: <T>Particle size characterization</T>,
            id: "psa-pos",
            modules: [
                EModules.PsaVelocityPos,
                EModules.PsaIntensityPos,
                EModules.PsaVolumeWeightedPos,
                EModules.PsaNumberWeightedPos,
            ],
        },
        {
            header: <T>PSA constant time</T>,
            id: "psa-time",
            modules: [
                EModules.PsaVelocityTime,
                EModules.PsaIntensityTime,
                EModules.PsaVolumeWeightedTime,
                EModules.PsaNumberWeightedTime,
            ],
        },
        {
            header: <T>Advanced analysis</T>,
            id: "advanced",
            modules: [
                EModules.Filtration,
                EModules.SedimentationHeight,
                EModules.FirstDerivative,
                EModules.MwlRatio,
                EModules.Astm,
            ],
        },
        {
            header: <T>Multiple wavelengths PSA</T>,
            id: "mwl",
            modules: [
                EModules.PsaMwlVolumeWeighted,
                EModules.PsaMwlNumberWeighted,
            ],
        },
    );

    for (const group of groups) {
        group.modules = group.modules.filter((module) =>
            modules.modules.includes(module),
        );
    }

    return groups.filter((x) => x.modules.length > 0);
}

export async function getAvailableModulesByTask(
    targets: string,
    searchParams?: {deviceClass: number; wavelengths: number},
): Promise<IModuleGroupDevices | undefined> {
    let groups: IModuleTaskGroup[] = [];

    if (!targets && !searchParams) {
        return;
    }

    let modulesResponse: IModulesResponse;
    try {
        modulesResponse = await http
            .get(`/api/centrifuge/analysis/modules?${targets}`, {searchParams})
            .json<IModulesResponse>();
    } catch {
        return;
    }

    const isXrayOnly = modulesResponse.devices.every(
        (x) => x === EDeviceClasses.XRay,
    );

    groups.push(
        {
            header: (
                <React.Fragment>
                    <T>Dispersion characterization</T>
                </React.Fragment>
            ),
            id: "stability",
            modules: [
                {
                    item: {
                        module: EModules.Instability,
                        task: EAnalysisTasks.StabilityIndex,
                    },
                },
                {
                    item: {
                        module: EModules.FrontTracking,
                        task: EAnalysisTasks.FrontTrackingComparative,
                    },
                },
                {
                    item: {
                        module: EModules.FrontTracking,
                        task: EAnalysisTasks.FrontTrackingPredictive,
                    },
                },
            ],
        },
        {
            header: <T>Particle characterization</T>,
            id: "psa",
            modules: [
                {
                    item: {
                        module: EModules.PsaVelocityPos,
                        task: EAnalysisTasks.PsaVelocity,
                    },
                },
                {
                    item: {
                        header: <T>Particle size distribution</T>,
                        id: "psd",
                        modules: [
                            {
                                item: {
                                    module: EModules.PsaIntensityPos,
                                    task: EAnalysisTasks.ExtinctionWeighted,
                                },
                            },
                            {
                                item: {
                                    module: EModules.PsaVolumeWeightedPos,
                                    task: isXrayOnly
                                        ? EAnalysisTasks.ExtinctionWeighted
                                        : EAnalysisTasks.VolumeWeighted,
                                },
                            },
                            {
                                item: {
                                    module: EModules.PsaNumberWeightedPos,
                                    task: EAnalysisTasks.NumberWeighted,
                                },
                            },
                            {
                                item: {
                                    header: <T>Multiple wavelengths</T>,
                                    id: "mwl",
                                    modules: [
                                        {
                                            item: {
                                                module: EModules.PsaMwlVolumeWeighted,
                                                task: EAnalysisTasks.MwlVolumeWeighted,
                                            },
                                        },
                                        {
                                            item: {
                                                module: EModules.PsaMwlNumberWeighted,
                                                task: EAnalysisTasks.MwlNumberWeighted,
                                            },
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                },
            ],
        },
        {
            header: <T>Separation</T>,
            id: "separation",
            modules: [
                {
                    item: {
                        module: EModules.LumView,
                        task: EAnalysisTasks.LumView,
                    },
                },
                {
                    item: {
                        module: EModules.Integration,
                        task: EAnalysisTasks.Integration,
                    },
                },
                {
                    item: {
                        module: EModules.FrontTracking,
                        task: EAnalysisTasks.FrontTracking,
                    },
                },
                {
                    item: {
                        module: EModules.FirstDerivative,
                        task: EAnalysisTasks.FirstDerivative,
                    },
                },
                {
                    item: {
                        module: EModules.MwlRatio,
                        task: EAnalysisTasks.MWLExtinctionRatio,
                    },
                },
                {
                    item: {
                        module: EModules.Filtration,
                        task: EAnalysisTasks.Filtration,
                    },
                },
                {
                    item: {
                        module: EModules.SedimentationHeight,
                        task: EAnalysisTasks.SedimentationHeight,
                    },
                },
            ],
        },
    );

    function filterGroup(
        group: IModuleTaskGroup,
        allowedModules: EModules[],
        allowedTasks: EAnalysisTasks[],
    ): IModuleTaskGroup {
        return {
            ...group,
            modules: group.modules
                .map((entry) => {
                    const item = entry.item;
                    if ("module" in item) {
                        return allowedModules.includes(item.module) &&
                            allowedTasks.includes(item.task)
                            ? entry
                            : null;
                    }
                    const filteredGroup = filterGroup(
                        item,
                        allowedModules,
                        allowedTasks,
                    );
                    return filteredGroup.modules.length > 0
                        ? {...entry, item: filteredGroup}
                        : null;
                })
                .filter(
                    (entry): entry is IModuleTaskGroupEntry => entry !== null,
                ),
        };
    }

    groups = groups
        .map((group) =>
            filterGroup(group, modulesResponse.modules, modulesResponse.tasks),
        )
        .filter((group) => group.modules.length > 0);

    const result: IModuleGroupDevices = {
        modules: groups,
        devices: modulesResponse.devices,
    };

    return result;
}

interface ICentAnalyzeButtonProps {
    project: number;
    targets: string;

    className?: string;
    hrefTarget?: string;

    getTargets?(): (engine?: EModules) => string;
}

class CentAnalyzeButton extends React.PureComponent<ICentAnalyzeButtonProps> {
    public render() {
        const {hrefTarget, project, targets, className, getTargets} =
            this.props;
        const _getTargets = () => () => targets;

        return (
            <AnalyzeButton
                className={className}
                hrefTarget={hrefTarget}
                project={project}
                serverId={EServerId.Centrifuge}
                getModules={this.getModules}
                getTasks={this.getTasks}
                getTargets={getTargets ?? _getTargets}
            />
        );
    }

    private async getModules(targets: string) {
        return getAvailableModules(targets);
    }

    private async getTasks(targets: string) {
        return getAvailableModulesByTask(targets);
    }
}

export default CentAnalyzeButton;
